<template>
	<div class="checkbox-wrapper">
		<label
			:class="['checkbox-container', `align-${computedAlignment}`, `label-${computedLabelPosition}`, { 'disabled': computedDisabled }]">
			<span v-if="label && computedLabelPosition === 'left'" class="checkbox-label checkbox-label-left">
				<slot :checked="isChecked">{{ label }}</slot>
			</span>
			<input type="checkbox" v-model="isChecked" class="checkbox-input" :indeterminate="indeterminate"
				:disabled="computedDisabled" ref="checkboxRef">
			<span class="checkbox-custom" :class="{
				'indeterminate': indeterminate,
				'medium': computedSize === 'medium',
				'big': computedSize === 'big',
				'disabled': computedDisabled
			}"></span>
			<span v-if="label && computedLabelPosition === 'right'" class="checkbox-label">
				<slot :checked="isChecked">{{ label }}</slot>
			</span>
		</label>
	</div>
</template>


<script setup>
import { ref, computed, watch, onMounted, inject } from 'vue'

const props = defineProps({
	label: {
		type: String,
		default: 'label'
	},
	modelValue: {
		type: [Boolean, Number, String],
		default: false
	},
	value: {
		type: [String, Number, Boolean, Object],
		default: null
	},
	alignment: {
		type: String,
		default: 'middle',
		validator: (value) => ['top', 'middle', 'bottom'].includes(value)
	},
	labelPosition: {
		type: String,
		default: 'right',
		validator: (value) => ['left', 'right'].includes(value)
	},
	indeterminate: {
		type: Boolean,
		default: false
	},
	size: {
		type: String,
		default: 'default',
		validator: (value) => ['default', 'medium', 'big'].includes(value)
	},
	checked: {
		type: Boolean,
		default: false
	},
	checkedVal: {
		type: [Boolean, Number, String],
		default: true
	},
	uncheckedVal: {
		type: [Boolean, Number, String],
		default: false
	},
	disabled: {
		type: Boolean,
		default: false
	}
})

const emit = defineEmits(['update:modelValue'])
const checkboxRef = ref(null)

// Try to inject values from parent ZSCheckboxGroup
const checkboxGroupModel = inject('checkboxGroupModel', null)
const checkboxGroupChange = inject('checkboxGroupChange', null)
const checkboxGroupSize = inject('checkboxGroupSize', null)
const checkboxGroupAlignment = inject('checkboxGroupAlignment', null)
const checkboxGroupLabelPosition = inject('checkboxGroupLabelPosition', null)
const checkboxGroupDisabled = inject('checkboxGroupDisabled', null)

// Use injected values if available, otherwise use prop values
const computedSize = computed(() => (checkboxGroupSize?.value && props.size === 'default') ? checkboxGroupSize.value : props.size)
const computedAlignment = computed(() => (checkboxGroupAlignment?.value && props.alignment === 'middle') ? checkboxGroupAlignment.value : props.alignment)
const computedLabelPosition = computed(() => (checkboxGroupLabelPosition?.value && props.labelPosition === 'right') ? checkboxGroupLabelPosition.value : props.labelPosition)
const computedDisabled = computed(() => checkboxGroupDisabled?.value || props.disabled)

// Watch for indeterminate prop changes
watch(() => props.indeterminate, (val) => {
	if (checkboxRef.value) {
		checkboxRef.value.indeterminate = val;
	}
})

// Set indeterminate on mount
onMounted(() => {
	if (checkboxRef.value) {
		checkboxRef.value.indeterminate = props.indeterminate;
	}
})

const isChecked = computed({
	get: () => {
		if (checkboxGroupModel) {
			return Array.isArray(checkboxGroupModel.value) && checkboxGroupModel.value.includes(props.value);
		}
		if (props.modelValue !== undefined) {
			return props.modelValue === props.checkedVal;
		}
		return props.checked === props.checkedVal;
	},
	set: (value) => {
		if (checkboxGroupChange) {
			checkboxGroupChange(value, props.value);
		} else {
			const newValue = value ? props.checkedVal : props.uncheckedVal;
			emit('update:modelValue', newValue);
		}
	}
})
</script>


<style>
:root {
	/* Colors */
	--color-checkbox-label: #333;
	--color-checkbox-default-bg: #F9F9F9;
	--color-checkbox-default-border: #D0E0EE;
	--color-checkbox-medium-border: #eaeaea;
	--color-checkbox-big-border: #eee;
	--color-checkbox-big-shadow: rgba(0, 0, 0, 0.1);
	--color-checkbox-focus-shadow: rgba(176, 229, 245, 0.3);
	--color-blue: #4384BD;

	/* CheckBox Base Styles */
	--zs-checkbox-padding: 8px;
	--zs-checkbox-label-padding-left: 20px;
	--zs-checkbox-transition: all 0.2s ease;
	--zs-checkbox-label-color: var(--color-checkbox-label);
	--zs-checkbox-label-font-size: 12px;
	--zs-checkbox-label-margin-left: 10px;
	--zs-checkbox-label-font-family: "Poppins";

	/* CheckBox Default Size */
	--zs-checkbox-default-size: 18px;
	--zs-checkbox-default-border-radius: 5px;
	--zs-checkbox-default-bg: var(--color-checkbox-default-bg);
	--zs-checkbox-default-border: 1px solid var(--color-checkbox-default-border);

	/* CheckBox Medium Size */
	--zs-checkbox-medium-size: 22px;
	--zs-checkbox-medium-border: 1px solid var(--color-checkbox-medium-border);

	/* CheckBox Big Size */
	--zs-checkbox-big-size: 28px;
	--zs-checkbox-big-shadow: 0 0px 5px var(--color-checkbox-big-shadow);
	--zs-checkbox-big-border: 1px solid var(--color-checkbox-big-border);

	/* CheckBox Check Mark */
	--zs-checkbox-checkmark-color: var(--color-blue);

	/* Default Size Checkmark */
	--zs-checkbox-default-checkmark-width: 4px;
	--zs-checkbox-default-checkmark-height: 8px;
	--zs-checkbox-default-checkmark-left: 5px;
	--zs-checkbox-default-checkmark-top: 2px;

	/* Medium Size Checkmark */
	--zs-checkbox-medium-checkmark-width: 6px;
	--zs-checkbox-medium-checkmark-height: 12px;
	--zs-checkbox-medium-checkmark-left: 7px;
	--zs-checkbox-medium-checkmark-top: 1px;

	/* Big Size Checkmark */
	--zs-checkbox-big-checkmark-width: 8px;
	--zs-checkbox-big-checkmark-height: 16px;
	--zs-checkbox-big-checkmark-left: 8px;
	--zs-checkbox-big-checkmark-top: 0px;

	/* Indeterminate Marks */
	--zs-checkbox-indeterminate-height: 2px;
	--zs-checkbox-default-indeterminate-width: 10px;
	--zs-checkbox-default-indeterminate-left: 3px;
	--zs-checkbox-default-indeterminate-top: 7px;

	--zs-checkbox-medium-indeterminate-width: 12px;
	--zs-checkbox-medium-indeterminate-left: 5px;
	--zs-checkbox-medium-indeterminate-top: 10px;

	--zs-checkbox-big-indeterminate-width: 14px;
	--zs-checkbox-big-indeterminate-left: 6px;
	--zs-checkbox-big-indeterminate-top: 12px;

	/* Focus Style */
	--zs-checkbox-focus-shadow: 0 0 0 2px var(--color-checkbox-focus-shadow);
}

.checkbox-wrapper {
	font-family: "Satoshi-Light", sans-serif;
	padding: var(--zs-checkbox-padding);
}

.checkbox-container {
	display: flex;
	cursor: pointer;
	position: relative;
	padding-left: var(--zs-checkbox-label-padding-left);
	user-select: none;
	width: 100%;

	&:has(.checkbox-custom.big){
		padding-left: 30px;
	}

	&.label-left {
		padding-left: 0;
		padding-right: var(--zs-checkbox-label-padding-left);
		flex-direction: row-reverse;

		.checkbox-custom {
			right: 0;
			left: auto;
		}
		&.big{
			padding-right: 30px;
		}
	}

	&.align-top {
		align-items: flex-start;

		.checkbox-label {
			margin-top: -2px;
		}
	}

	&.align-middle {
		align-items: center;
	}

	&.align-bottom {
		align-items: flex-end;
	}
}

.checkbox-input {
	position: absolute;
	opacity: 0;
	cursor: pointer;
	height: 0;
	width: 0;
}

.checkbox-custom {
	position: absolute;
	left: 0;
	height: var(--zs-checkbox-default-size);
	width: var(--zs-checkbox-default-size);
	background-color: var(--zs-checkbox-default-bg);
	border: var(--zs-checkbox-default-border);
	border-radius: var(--zs-checkbox-default-border-radius);
	box-shadow: none;
	transition: var(--zs-checkbox-transition);

	&.medium {
		background-color: var(--zs-checkbox-default-bg);
		border: var(--zs-checkbox-medium-border);
		height: var(--zs-checkbox-medium-size);
		width: var(--zs-checkbox-medium-size);
	}

	&.big {
		background-color: white;
		box-shadow: var(--zs-checkbox-big-shadow);
		border: var(--zs-checkbox-big-border);
		height: var(--zs-checkbox-big-size);
		width: var(--zs-checkbox-big-size);
	}
}

.checkbox-input:checked~.checkbox-custom {
	background-color: white;
	border-color: var(--zs-checkbox-checkmark-color);
}

.checkbox-custom.medium:after {
	content: "";
	position: absolute;
	display: none;
	left: var(--zs-checkbox-medium-checkmark-left);
	top: var(--zs-checkbox-medium-checkmark-top);
	width: var(--zs-checkbox-medium-checkmark-width);
	height: var(--zs-checkbox-medium-checkmark-height);
	border: solid var(--zs-checkbox-checkmark-color);
	border-width: 0 2px 2px 0;
	transform: rotate(45deg);
}

.checkbox-custom.big:after {
	content: "";
	position: absolute;
	display: none;
	left: var(--zs-checkbox-big-checkmark-left);
	top: var(--zs-checkbox-big-checkmark-top);
	width: var(--zs-checkbox-big-checkmark-width);
	height: var(--zs-checkbox-big-checkmark-height);
	border: solid var(--zs-checkbox-checkmark-color);
	border-width: 0 2px 2px 0;
	transform: rotate(45deg);
}

.checkbox-custom:after {
	content: "";
	position: absolute;
	display: none;
	left: var(--zs-checkbox-default-checkmark-left);
	top: var(--zs-checkbox-default-checkmark-top);
	width: var(--zs-checkbox-default-checkmark-width);
	height: var(--zs-checkbox-default-checkmark-height);
	border: solid var(--zs-checkbox-checkmark-color);
	border-width: 0 2px 2px 0;
	transform: rotate(45deg);
}

.checkbox-input:checked~.checkbox-custom:after {
	display: block;
}

.checkbox-custom.indeterminate.medium:after {
	content: "";
	position: absolute;
	display: block;
	left: var(--zs-checkbox-medium-indeterminate-left);
	top: var(--zs-checkbox-medium-indeterminate-top);
	width: var(--zs-checkbox-medium-indeterminate-width);
	height: var(--zs-checkbox-indeterminate-height);
	background: var(--zs-checkbox-checkmark-color);
	border: none;
	transform: none;
}

.checkbox-custom.indeterminate.big:after {
	content: "";
	position: absolute;
	display: block;
	left: var(--zs-checkbox-big-indeterminate-left);
	top: var(--zs-checkbox-big-indeterminate-top);
	width: var(--zs-checkbox-big-indeterminate-width);
	height: var(--zs-checkbox-indeterminate-height);
	background: var(--zs-checkbox-checkmark-color);
	border: none;
	transform: none;
}

.checkbox-custom.indeterminate:after {
	content: "";
	position: absolute;
	display: block;
	left: var(--zs-checkbox-default-indeterminate-left);
	top: var(--zs-checkbox-default-indeterminate-top);
	width: var(--zs-checkbox-default-indeterminate-width);
	height: var(--zs-checkbox-indeterminate-height);
	background: var(--zs-checkbox-checkmark-color);
	border: none;
	transform: none;
}

.checkbox-label {
	color: var(--zs-checkbox-label-color);
	font-size: var(--zs-checkbox-label-font-size);
	margin-left: var(--zs-checkbox-label-margin-left);
	font-family: var(--zs-checkbox-label-font-family);
	text-align: justify;
}

.checkbox-label-left {
	margin-left: 0;
	margin-right: var(--zs-checkbox-label-margin-left);
}

/* Focus styles for accessibility */
.checkbox-input:focus~.checkbox-custom {
	box-shadow: var(--zs-checkbox-focus-shadow);
}

/* Disabled styles */
.checkbox-container.disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

.checkbox-custom.disabled {
	background-color: #f5f5f5;
	border-color: #d9d9d9;
}

.checkbox-input:disabled {
	cursor: not-allowed;
}
</style>